import { animated, useSpring } from '@react-spring/web';
import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import { trackGAEvent } from '@/utility/analytics';
import { openInNewTab } from '@/utility/helpers';
import { getCurrentLanguageLabel, t } from '@/utility/localization';
import { NASIO_LIGHT_IMAGE_DATA } from '@/utility/member/constants';
import {
  START_COMMUNITY_ONBOARDING_ROUTE,
  openAppDownloadPage
} from '@/utility/routesHelper';

import { useAuthContext } from '@/contexts/AuthContext';

import Image from '@/components/common/Image';
import NextImage from '@/components/common/NextImage';
import PopOverModal from '@/components/common/PopOverModal';
import ProgressRing from '@/components/common/ProgressRing';
import { showToast } from '@/components/common/ToastContainer';
import Icon from '@/components/npl/Icon';
import NPLButton from '@/components/npl/NPLButton';
import NPLIconButton from '@/components/npl/NPLIconButton';

import useLocale from '@/hooks/useLocale';

import {
  SETTINGS_PAGE_MEMBERSHIP_PATH,
  SETTINGS_PAGE_PROFILE_PATH
} from '@/pages/user/constants';

import getConstants from '../../constants';
import {
  trackOpenManagerViewBtnClick,
  trackOpenMemberViewBtnClick,
  trackOpenProfileSettingBtnClick
} from '../../utils';
import LanguageSelectorBottomSheet from '../LanguageSelectorBottomSheet';

const DrawerNavbar = ({
  user,
  activeCommunity,
  navigationItems,
  communityList,
  handleLogout
}) => {
  const router = useRouter();
  const { isMemberView, openManagerView } = useAuthContext();
  const { currentLocale } = useLocale();
  const { NASIO_HELP_URL } = getConstants();
  const [isDrawerNavbarOpen, setIsDrawerNavbarOpen] = useState(false);
  const [showProfilePopover, setShowProfilePopover] = useState(false);
  const [isLanguageSelectorOpened, setIsLanguageSelectorOpened] =
    useState(false);

  const drawerNavbarTransitions = useSpring({
    transform: isDrawerNavbarOpen ? 'translateX(0%)' : 'translateX(-100%)'
  });

  const thumbnailImgData = activeCommunity?.thumbnailImgData;
  const activeHeaderLabel =
    navigationItems?.find((item) => item.isActive)?.label ?? '';
  const activeCommunityTitle = activeCommunity?.title;
  const activeCommunityHost = activeCommunity?.By;
  const profileImage = {
    alt: 'user',
    mobileImgData: {
      src: user?.learner?.profileImage,
      webpSrc: user?.learner?.profileImage,
      meta: {
        width: 40,
        height: 40
      }
    }
  };

  const toggleDrawerNavbar = () => setIsDrawerNavbarOpen((prev) => !prev);
  const toggleProfilePopover = () =>
    setShowProfilePopover((prev) => !prev);

  const handleMemberViewBtnClick = () => {
    if (isMemberView) {
      trackOpenManagerViewBtnClick(user, activeCommunity);
      openManagerView();
    } else {
      trackOpenMemberViewBtnClick(user, activeCommunity);
      router.push(activeCommunity?.link);
    }
  };

  const handleProfileBtnClick = () => {
    trackOpenProfileSettingBtnClick(user, activeCommunity);
    router.push(SETTINGS_PAGE_PROFILE_PATH);
  };

  const handleMyMembershipsClick = () => {
    trackGAEvent('clicked_my_memberships', { email: user?.email });
    router.push(SETTINGS_PAGE_MEMBERSHIP_PATH);
  };

  const handleOnClickLanguageSelector = () => {
    setIsLanguageSelectorOpened(true);
    setShowProfilePopover(false);
  };

  const renderProfilePopoverMenu = () => {
    const profileImage = {
      alt: 'user',
      mobileImgProps: {
        src: user?.learner?.profileImage,
        layout: 'fill',
        objectFit: 'cover'
      }
    };
    const fullName = `${user?.learner?.firstName ?? ''} ${
      user?.learner?.lastName ?? ''
    }`;

    return (
      <div>
        <div className="flex items-center space-x-12 px-8 py-12">
          <div className="flex-shrink-0 relative h-[40px] w-[40px] overflow-hidden rounded-full">
            <NextImage {...profileImage} />
          </div>
          <div className="space-y-4">
            <p className="max-w-[1000px] line-clamp-2 text-label-md font-medium text-[#1b1b1b]">
              {fullName}
            </p>
            <p className="truncate text-label-md text-[#1b1b1b] opacity-60">
              {user?.email}
            </p>
          </div>
        </div>

        <div className="-mx-16 my-12 border-t-1 border-npl-neutral-light-solid-6" />
        {!activeCommunity?.isDemo && activeCommunity?.isManager && (
          <>
            <div
              role="button"
              tabIndex={0}
              className="flex space-x-12 px-8 py-14 text-label-lg text-[#1b1b1b]"
              onClick={handleMemberViewBtnClick}>
              <Icon
                name="user-circle"
                width={18}
                height={18}
                fillOpacity={0.65}
              />
              <span>
                {isMemberView
                  ? t('open-manager-view')
                  : t('view-as-member')}
              </span>
            </div>

            <div className="-mx-16 my-12 border-t-1 border-npl-neutral-light-solid-6" />
          </>
        )}
        <div>
          <div
            role="button"
            tabIndex={0}
            onClick={handleProfileBtnClick}
            className="flex space-x-12 px-8 py-14 text-label-lg text-[#1b1b1b]">
            <Icon
              name="user-01"
              width={18}
              height={18}
              fillOpacity={0.65}
            />
            <span>{t('profile-settings')}</span>
          </div>

          <div
            role="button"
            tabIndex={0}
            data-test-id="membership-button"
            onClick={handleMyMembershipsClick}
            className="flex space-x-12 px-8 py-14 text-label-lg text-[#1b1b1b]">
            <Icon
              name="settings-04"
              width={18}
              height={18}
              fillOpacity={0.65}
            />
            <span>{t('my-memberships')}</span>
          </div>

          <div
            role="button"
            tabIndex={0}
            onClick={handleOnClickLanguageSelector}
            className="flex space-x-12 px-8 py-14 text-label-lg text-[#1b1b1b]">
            <Icon
              name="globe-02"
              width={18}
              height={18}
              fillOpacity={0.65}
            />
            <span>{`${t('language')}: ${getCurrentLanguageLabel(
              currentLocale
            )}`}</span>
            <Icon
              name="chevron-right"
              width={18}
              height={18}
              fillOpacity={0.65}
              className="!ml-auto"
            />
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              toggleProfilePopover();
              handleLogout();
            }}
            className="flex space-x-12 px-8 py-14 text-label-lg text-[#1b1b1b]">
            <Icon
              name="log-out"
              width={18}
              height={18}
              fillOpacity={0.65}
            />
            <span>{t('logout')}</span>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (isDrawerNavbarOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = null;
    };
  }, [isDrawerNavbarOpen]);

  return (
    <>
      <div className="sticky top-0 z-20 h-[72px] w-full border-b-1 border-neutral-90 bg-white-default font-poppins">
        <div className="flex h-full w-full items-center justify-between space-x-[28px] px-16">
          <div className="flex flex-1 items-center justify-between">
            <div
              role="button"
              tabIndex={0}
              className="flex items-center space-x-[12px]"
              onClick={toggleDrawerNavbar}>
              <div className="relative h-40 w-40 cursor-pointer">
                <div className="aspect-square h-40 w-40 overflow-hidden rounded-8">
                  <Image
                    {...thumbnailImgData}
                    customClassNames="w-40 h-40 object-cover aspect-square"
                  />
                </div>
                <div className="absolute bottom-0 right-0 flex h-[18px] w-[18px] translate-x-1/3 translate-y-1/3 items-center justify-center rounded-full bg-neutral-90">
                  <Icon name="menu-01" width={14} height={14} />
                </div>
              </div>
              <p className="text-heading-xs font-semibold text-neutral-10">
                {activeHeaderLabel}
              </p>
            </div>
            <div className="flex items-center">
              <div
                role="button"
                tabIndex={0}
                className="h-[40px] w-[40px] cursor-pointer min-h-[40px] min-w-[40px]"
                onClick={toggleProfilePopover}>
                <div className="overflow-hidden rounded-full">
                  <Image
                    {...profileImage}
                    customImgClassname="rounded-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <animated.div
          style={drawerNavbarTransitions}
          className="fixed left-0 top-0 z-over-intercom h-screen w-screen">
          <div className="flex h-full w-full bg-neutral-95 pb-32 pr-16 pt-24">
            <div className="flex w-[80px] flex-shrink-0 flex-col justify-between">
              <div className="flex-grow overflow-y-auto">
                <div className="space-y-[20px]">
                  {communityList?.map((community, index) => {
                    const { thumbnailImgData, onClick, isActive } =
                      community;

                    return (
                      <div
                        key={index}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          toggleDrawerNavbar();
                          onClick();
                        }}
                        className={`relative w-[68px] cursor-pointer rounded-b-8 rounded-r-8 p-4 pl-16 ${
                          isActive ? 'bg-npl-yellow-light-solid-4' : ''
                        }`}>
                        <div className="h-48 w-48 flex-shrink-0 overflow-hidden rounded-8">
                          <Image
                            {...thumbnailImgData}
                            customClassNames="object-cover"
                          />
                        </div>
                        {isActive && (
                          <div className="absolute left-0 top-0 h-full w-[3px] rounded-b-4 rounded-r-4 bg-npl-yellow-light-solid-9"></div>
                        )}
                      </div>
                    );
                  })}
                  <div className="cursor-pointer p-4 pb-40 pl-16">
                    <Link
                      href={START_COMMUNITY_ONBOARDING_ROUTE}
                      passHref
                      legacyBehavior>
                      <NPLButton
                        leadIcon="plus"
                        rounded
                        hierarchy="neutral_primary"
                        size="lg"
                        onClick={() =>
                          showToast({
                            text: t(
                              'you-will-be-logged-out-to-start-your-new-community'
                            )
                          })
                        }
                      />
                    </Link>
                  </div>
                </div>
              </div>
              <div className="relative mt-8">
                <div className="flex flex-col items-center space-y-24">
                  <div
                    role="button"
                    tabIndex={0}
                    className="flex h-[48px] w-[48px] cursor-pointer items-center justify-center rounded-full bg-neutral-90"
                    onClick={() => openInNewTab(NASIO_HELP_URL)}>
                    <Icon name="help-circle" width={32} height={32} />
                  </div>
                  <div
                    role="button"
                    tabIndex={0}
                    className="h-[48px] w-[48px] cursor-pointer"
                    onClick={toggleProfilePopover}>
                    <div className="overflow-hidden rounded-full">
                      <Image
                        {...profileImage}
                        customImgClassname="rounded-full"
                      />
                    </div>
                  </div>
                </div>
                <div className="to-[rgba(240, 241, 244, 0)] bg-gradient-to-t pointer-events-none absolute top-0 mt-0 h-[116px] w-full -translate-y-full from-neutral-95" />
              </div>
            </div>
            <div className="relative flex flex-1 flex-col justify-between overflow-scroll rounded-28 bg-white-default p-16 pt-40">
              <div>
                <div className="mx-8 mb-[24px]">
                  <p className="mb-4 text-heading-xs font-semibold text-neutral-10">
                    {activeCommunityTitle}
                  </p>
                  <p className="text-label-lg text-neutral-50">
                    {activeCommunityHost}
                  </p>
                </div>
                <div className="space-y-8">
                  {navigationItems?.map((navigationItem, index) => (
                    <>
                      {index !== 0 && (
                        <div>
                          <div className="mx-8 my-12 border-t-1 border-npl-neutral-light-solid-4" />
                        </div>
                      )}

                      {navigationItem.title && (
                        <span className="mb-8 ml-12 text-overline-xs font-semibold uppercase text-npl-neutral-light-solid-8">
                          {navigationItem.title}
                        </span>
                      )}

                      {navigationItem.items.map((item, index) => {
                        const {
                          label,
                          link,
                          isActive,
                          Icon,
                          rightIcon,
                          show,
                          showProgressRing,
                          onDismissalCtaClick,
                          progress,
                          isNew
                        } = item;

                        const colorClassName = isActive
                          ? 'fill-npl-yellow-light-alpha-11 text-npl-yellow-light-alpha-11'
                          : 'fill-npl-text-secondary-on-light text-npl-text-secondary-on-light';

                        const onClick = () => {
                          setIsDrawerNavbarOpen(false);
                          router.push(link);
                        };

                        if (!show) return null;

                        return (
                          <>
                            <div
                              key={index}
                              role="button"
                              tabIndex={0}
                              onClick={onClick}
                              className={`flex cursor-pointer items-center space-x-16 rounded-full p-8 ${
                                isActive
                                  ? 'bg-npl-yellow-light-alpha-3'
                                  : ''
                              }`}>
                              <span>
                                {Icon && (
                                  <Icon
                                    className={colorClassName}
                                    width="20"
                                    height="20"
                                  />
                                )}

                                {showProgressRing ? (
                                  <ProgressRing progress={progress} />
                                ) : null}
                              </span>
                              <p
                                className={classNames(
                                  'flex-grow text-label-lg font-medium',
                                  colorClassName
                                )}>
                                {label}
                              </p>
                              {rightIcon && (
                                <div className="justify-self-end">
                                  {rightIcon()}
                                </div>
                              )}
                              {isNew && (
                                <p className="flex items-center space-x-2 rounded-4 bg-npl-yellow-light-solid-3 px-2 text-overline-xs font-semibold text-npl-yellow-light-solid-11">
                                  <Icon
                                    name="stars-filled-02"
                                    width="12"
                                    height="12"
                                    className="fill-npl-yellow-light-solid-11"
                                  />
                                  <span>{t('new1')}</span>
                                </p>
                              )}
                              {onDismissalCtaClick && isActive && (
                                <div className="ml-auto flex items-center">
                                  <NPLIconButton
                                    size="xs"
                                    hierarchy="plain"
                                    icon="x-close"
                                    onClick={onDismissalCtaClick}
                                  />
                                </div>
                              )}
                            </div>
                          </>
                        );
                      })}
                    </>
                  ))}
                </div>
              </div>
              {isMemberView && (
                <div>
                  <div
                    className="flex items-center rounded-full bg-dark-75 px-[22px] py-[19px]"
                    onClick={openAppDownloadPage}>
                    <div className="h-[22px] w-[22px] shadow-md">
                      <Image
                        {...NASIO_LIGHT_IMAGE_DATA}
                        customClassNames="rounded-4"
                      />
                    </div>
                    <p className="flex-grow text-center text-label-md font-medium text-neutral-99">
                      {t('get-the-app')}
                    </p>
                  </div>
                </div>
              )}
              <div
                role="button"
                tabIndex={0}
                className="absolute right-20 top-20 cursor-pointer"
                onClick={toggleDrawerNavbar}>
                <Icon name="x-close" />
              </div>
            </div>
          </div>
        </animated.div>
        <LanguageSelectorBottomSheet
          open={isLanguageSelectorOpened}
          onClose={() => {
            setIsLanguageSelectorOpened(false);
            setShowProfilePopover(true);
          }}
        />
        <PopOverModal
          showCloseIcon={false}
          open={showProfilePopover}
          onClose={() => setShowProfilePopover(false)}>
          {renderProfilePopoverMenu()}
        </PopOverModal>
      </div>
    </>
  );
};

export default DrawerNavbar;
